import React from "react";
import { useSelector } from "react-redux";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { SkubotSpinner } from "@commonsku/styles";
import OrderItem from "../../OrderItem";
import Overlay from "../../Overlay";
import createPopup from "../../../popup-factory";
import { getPopups } from "../../../selectors";
import { useCatalogItemHydration } from "./useCatalogItemHydration";

interface CatalogItemEditModalProps {
    catalogId: string;
    itemId: string;
    onClose: () => void;
}

const catalogProjectStub = {
    zip2tax: 0,
    show_product_warnings: 0,
};

/** Above ClientApp global Overlay (~1006). Nested popups use popupBaseZIndex > shell. */
const CATALOG_SHELL_Z_INDEX = 1100;
/** Must exceed shell — @commonsku/styles Popup portals to document.body. */
const CATALOG_POPUP_BASE_Z_INDEX = CATALOG_SHELL_Z_INDEX + 1;
const CATALOG_CLOSE_Z_INDEX = 50;

const shellStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: CATALOG_SHELL_Z_INDEX,
    isolation: "isolate",
};

const modalStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    background: "white",
    overflow: "auto",
};

const popupLayerStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 10,
};

const closeButtonStyle: React.CSSProperties = {
    position: "fixed",
    right: "1rem",
    top: "1rem",
    zIndex: CATALOG_CLOSE_Z_INDEX,
};

const loadingStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "50vh",
};

export const CatalogItemEditModal = ({
    catalogId,
    itemId,
    onClose,
}: CatalogItemEditModalProps) => {
    const popups = useSelector(getPopups);
    const { ready, order } = useCatalogItemHydration(catalogId, itemId);

    return (
        <div style={shellStyle} role="dialog" aria-modal="true" aria-label="Edit catalog item">
            <div style={modalStyle}>
                {ready ? (
                    <DndProvider backend={HTML5Backend}>
                        <OrderItem
                            key={itemId}
                            order={order}
                            project={catalogProjectStub}
                            params={{ item_id: itemId }}
                            popups={popups}
                            hideMarginAndTaxes
                            suppressCloseButton
                            popupBaseZIndex={CATALOG_POPUP_BASE_Z_INDEX}
                        />
                    </DndProvider>
                ) : (
                    <div style={loadingStyle}>
                        <SkubotSpinner />
                    </div>
                )}
            </div>
            {popups?.length > 0 ? (
                <div style={popupLayerStyle}>
                    <Overlay
                        popups={popups}
                        popupBaseZIndex={CATALOG_POPUP_BASE_Z_INDEX}
                    />
                    {popups.map((popup, index) =>
                        createPopup(popup, index, {
                            popupBaseZIndex: CATALOG_POPUP_BASE_Z_INDEX,
                        }),
                    )}
                </div>
            ) : (
                <a className="button" style={closeButtonStyle} onClick={onClose}>
                    Close
                </a>
            )}
        </div>
    );
};
